import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Briefcase, Search, Upload, CheckCircle2, DollarSign, Clock } from "lucide-react";
import { PageHeader } from "../components/ui-blocks";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { JOBS, JOB_TYPES, INDUSTRIES } from "../data";

export default function JobSeekers() {
  const [type, setType] = useState("All");
  const [industry, setIndustry] = useState("All");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const industries = ["All", ...INDUSTRIES.map((i) => i.name)];
  const filtered = useMemo(() => JOBS.filter((j) =>
    (type === "All" || j.type === type) &&
    (industry === "All" || j.industry === industry) &&
    (query === "" || (j.title + j.location + j.tags.join(" ")).toLowerCase().includes(query.toLowerCase()))
  ), [type, industry, query]);

  return (
    <div data-testid="job-seekers-page">
      <PageHeader overline="For Job Seekers" title="Find work worth staying for" subtitle="Browse curated openings across six industries. Apply in minutes — a recruiter reviews every application personally." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter bar */}
          <Reveal className="rounded-2xl border border-slate-800/80 bg-[#111827]/70 p-4 sm:p-5 mb-8 sticky top-24 z-30 backdrop-blur-xl">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input data-testid="job-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title, skill, location…" className="input-dark pl-10" />
              </div>
              <div className="flex flex-wrap gap-2">
                {JOB_TYPES.map((t) => (
                  <button key={t} data-testid={`filter-type-${t.toLowerCase()}`} onClick={() => setType(t)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${type === t ? "bg-amber-400 text-slate-950" : "border border-slate-700 text-slate-300 hover:border-slate-500"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-800">
              {industries.map((ind) => (
                <button key={ind} data-testid={`filter-industry-${ind.split(" ")[0].toLowerCase()}`} onClick={() => setIndustry(ind)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${industry === ind ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}>{ind}</button>
              ))}
            </div>
          </Reveal>

          <div className="text-sm text-slate-500 font-mono mb-5" data-testid="job-count">{filtered.length} open role{filtered.length !== 1 ? "s" : ""}</div>

          <Stagger className="grid grid-cols-1 lg:grid-cols-2 gap-5" key={type + industry + query}>
            {filtered.map((job) => (
              <StaggerItem key={job.id}>
                <div data-testid={`job-card-${job.id}`} className="group h-full rounded-2xl border border-slate-800/80 bg-[#111827]/70 p-6 sm:p-7 hover:border-amber-500/40 transition-all duration-300 flex flex-col">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white group-hover:text-amber-400 transition-colors">{job.title}</h3>
                      <div className="font-mono text-xs text-slate-500 mt-1">{job.industry}</div>
                    </div>
                    <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-full px-2.5 py-1">{job.type}</span>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed mb-5 flex-1">{job.desc}</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-400 mb-5">
                    <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-amber-500" />{job.location}</span>
                    <span className="flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-amber-500" />{job.salary}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-500" />{job.posted}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {job.tags.map((t) => <span key={t} className="text-xs font-mono text-slate-400 border border-slate-700 rounded-md px-2 py-1">{t}</span>)}
                  </div>
                  <button data-testid={`job-apply-open-${job.id}`} onClick={() => setActive(job)} className="mt-auto w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-slate-950 bg-gradient-to-r from-amber-400 to-amber-500 rounded-xl hover:shadow-lg hover:shadow-amber-500/30 transition-all active:scale-[0.99]"><Briefcase className="w-4 h-4" /> Apply Now</button>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          {filtered.length === 0 && <Reveal className="text-center py-20 text-slate-500">No roles match your filters. Try broadening your search.</Reveal>}
        </div>
      </section>

      <ApplyDialog job={active} onClose={() => setActive(null)} />
    </div>
  );
}

const ApplyDialog = ({ job, onClose }) => {
  const [sent, setSent] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File exceeds 5MB limit."); return; }
    if (!/\.(pdf|doc|docx)$/i.test(f.name)) { toast.error("Please upload a PDF or DOC file."); return; }
    setFile(f);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    if (!file) { toast.error("Please attach your resume."); return; }
    setSent(true);
    toast.success("Application submitted — good luck!");
  };

  const close = () => { onClose(); setTimeout(() => { setSent(false); setFile(null); setForm({ name: "", email: "", phone: "", note: "" }); }, 300); };

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-lg p-0 overflow-hidden" data-testid="apply-dialog">
        {job && (
          <div className="max-h-[85vh] overflow-y-auto">
            <div className="p-6 sm:p-8">
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div key="s" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-8 text-center" data-testid="apply-success">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15 border border-amber-500/30 mb-5"><CheckCircle2 className="w-8 h-8 text-amber-400" /></div>
                    <h3 className="text-2xl font-bold text-white mb-2">Application sent</h3>
                    <p className="text-slate-400">Thanks {form.name.split(" ")[0]}! Your application for <span className="text-amber-400">{job.title}</span> is in. A recruiter will be in touch soon.</p>
                    <button onClick={close} data-testid="apply-close" className="mt-7 px-5 py-2.5 text-sm font-semibold text-slate-950 bg-amber-400 rounded-xl">Done</button>
                  </motion.div>
                ) : (
                  <motion.form key="f" onSubmit={submit} className="space-y-4" data-testid="apply-form">
                    <div className="mb-2">
                      <div className="font-mono text-xs text-amber-400">{job.type} · {job.location}</div>
                      <h3 className="text-xl font-bold text-white mt-1">{job.title}</h3>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs text-slate-400">Full name *</Label><Input data-testid="apply-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="input-dark" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label className="text-xs text-slate-400">Email *</Label><Input data-testid="apply-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className="input-dark" /></div>
                      <div className="space-y-1.5"><Label className="text-xs text-slate-400">Phone</Label><Input data-testid="apply-phone" value={form.phone} onChange={set("phone")} placeholder="+1 (555) …" className="input-dark" /></div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-400">Resume (PDF/DOC, max 5MB) *</Label>
                      <label data-testid="resume-upload-label" className="flex items-center gap-3 rounded-xl border border-dashed border-slate-700 bg-[#0B0F19] px-4 py-4 cursor-pointer hover:border-amber-500/50 transition-all">
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400"><Upload className="w-5 h-5" /></span>
                        <span className="text-sm text-slate-400 truncate">{file ? <span className="text-white">{file.name}</span> : "Drag & drop or click to upload"}</span>
                        <input data-testid="resume-upload-input" type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" />
                      </label>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs text-slate-400">Cover note</Label><Textarea data-testid="apply-note" value={form.note} onChange={set("note")} rows={3} placeholder="Why you're a great fit…" className="input-dark resize-none" /></div>
                    <button type="submit" data-testid="apply-submit-btn" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-slate-950 bg-gradient-to-r from-amber-400 to-amber-500 rounded-xl hover:shadow-lg hover:shadow-amber-500/30 transition-all active:scale-[0.99]">Submit Application</button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
